import { useCallback, useMemo, useRef } from 'react';
import { useGitSyncStore } from '../store/gitSyncStore';
import { setGitSecrets } from '../store/uiModalStore';
import { useNotificationStore } from '../store/notificationStore';
import { saveScopeVersion, addScopeRemote, stopScopeTracking, REMOTE_HAS_OTHER_SCOPE } from '../services/git/scopeLocalGitSync';
import { withScopeProgress } from '../services/git/scopeProgress';

const notify = () => useNotificationStore.getState();
const message = (err) => err?.message || String(err);

/**
 * "My projects, boards & documents" tracked on this device (T10, 04.10.2026):
 * the Git page's Save version · Add a remote · Stop tracking for the personal
 * scope — the scope twin of useGitLocal (workspaces). Called from useScopeGit,
 * which owns the busy key, the links and the conflict dialog.
 *
 * @param {{ setBusyKey, reloadLinks, refreshStatus, setConflict }} deps
 */
export default function useScopeGitLocal({ setBusyKey, reloadLinks, refreshStatus, setConflict }) {
  // "Commit anyway" from the secret-scan dialog re-runs the CURRENT handler.
  const saveRef = useRef(null);
  const addRef = useRef(null);

  const secrets = (scope, findings, retry) => setGitSecrets({ workspace: { id: scope.key, name: scope.name }, findings, commitAnyway: retry });

  const saveVersion = useCallback(
    async (scope, { allowSecrets = false } = {}) => {
      setBusyKey(scope.key);
      try {
        // The history was missing (deleted by hand): this save starts a fresh one.
        const missing = useGitSyncStore.getState().byWorkspace[String(scope.key)]?.status?.initialized === false;
        const result = await withScopeProgress(scope.key, () => saveScopeVersion(scope, { allowSecrets }));
        if (result.status === 'secrets') {
          secrets(scope, result.findings, () => saveRef.current?.(scope, { allowSecrets: true }));
          return result;
        }
        useGitSyncStore.getState().recordSuccess(String(scope.key));
        if (missing) notify().info('The history was missing on this device — this version starts a new one.', scope.name);
        else if (result.commit) notify().success('Version saved on this device.', scope.name);
        else notify().info('Nothing new to save.', scope.name);
        if (result.git !== scope.git) await reloadLinks();
        await refreshStatus({ ...scope, git: result.git });
        return result;
      } catch (err) {
        useGitSyncStore.getState().recordError?.(String(scope.key), message(err));
        notify().error(message(err), `${scope.name}: not saved`);
        return null;
      } finally {
        setBusyKey(null);
      }
    },
    [setBusyKey, reloadLinks, refreshStatus]
  );
  saveRef.current = saveVersion;

  /**
   * From the Connect dialog in "Add a remote" mode: errors (a refusal) are
   * thrown back to it so it shows them in place; the history stays local.
   * @returns {Promise<boolean>} true when the dialog can close
   */
  const addRemote = useCallback(
    async (scope, opts = {}) => {
      setBusyKey(scope.key);
      try {
        const result = await withScopeProgress(scope.key, () => addScopeRemote(scope, opts));
        if (result.status === 'secrets') {
          secrets(scope, result.findings, () => addRef.current?.(scope, { ...opts, allowSecrets: true }).catch(() => {}));
          return true;
        }
        await reloadLinks();
        if (result.status === 'diverged' || result.status === 'not_applied') {
          useGitSyncStore.getState().recordError?.(String(scope.key), 'The remote moved on — try Add a remote again');
          notify().error('The repository changed meanwhile — nothing was linked, the history stays on this device. Try again.', `${scope.name}: remote not added`);
          return true;
        }
        if (result.status === 'conflicts') {
          setConflict({ scope: { ...scope, git: result.git }, conflicts: result.conflicts });
          return true;
        }
        useGitSyncStore.getState().recordSuccess(String(scope.key));
        notify().success('Remote added — every saved version was pushed.', scope.name);
        await refreshStatus({ ...scope, git: result.git });
        return true;
      } catch (err) {
        if (err?.code === REMOTE_HAS_OTHER_SCOPE) notify().error(message(err), `${scope.name}: remote not added`);
        throw err;
      } finally {
        setBusyKey(null);
      }
    },
    [setBusyKey, reloadLinks, refreshStatus, setConflict]
  );
  addRef.current = addRemote;

  const stopTracking = useCallback(
    async (scope) => {
      setBusyKey(scope.key);
      try {
        await stopScopeTracking(scope);
        useGitSyncStore.getState().forget(String(scope.key));
        notify().info('Stopped tracking — the saved versions on this device were deleted.', scope.name);
        await reloadLinks();
      } catch (err) {
        notify().error(message(err), `${scope.name}: still tracked`);
      } finally {
        setBusyKey(null);
      }
    },
    [setBusyKey, reloadLinks]
  );

  return useMemo(() => ({ saveVersion, addRemote, stopTracking }), [saveVersion, addRemote, stopTracking]);
}
